import React, { useEffect, useMemo, useState } from "react";
import API from "./api";
import { hasPermission } from "./rbac";

const BRAND = {
  red: "#D73C2E",
  redDeep: "#B9343A",
  ink: "#31374A",
  muted: "#6E7588",
  border: "rgba(215,60,46,0.14)",
  borderStrong: "rgba(215,60,46,0.22)",
  bg: "#FFF8F6",
  panel: "rgba(255,255,255,0.96)",
  shadow: "0 22px 56px rgba(185,52,58,0.12)",
};

const numberFormatter = new Intl.NumberFormat("en-IN");

function formatCount(value, loading) {
  if (loading) return "...";
  return numberFormatter.format(Number(value || 0));
}

function normalizeCollectionPayload(data) {
  if (Array.isArray(data)) {
    return { items: data, total: data.length, stats: {} };
  }
  return data || {};
}

function MetricCard({ label, value, onClick, compact = false }) {
  const clickable = typeof onClick === "function";
  const Component = clickable ? "button" : "div";

  return (
    <Component
      type={clickable ? "button" : undefined}
      style={{
        ...S.metricCard,
        ...(compact ? S.metricCardCompact : {}),
        ...(clickable ? S.metricCardButton : {}),
      }}
      onClick={clickable ? onClick : undefined}
    >
      <div style={{ ...S.metricValue, ...(compact ? S.metricValueCompact : {}) }}>{value}</div>
      <div style={{ ...S.metricLabel, ...(compact ? S.metricLabelCompact : {}) }}>{label}</div>
    </Component>
  );
}

function ActionButton({ label, onClick }) {
  return (
    <button type="button" style={S.actionButton} onClick={onClick}>
      {label}
    </button>
  );
}

export default function DashboardHome({
  user = null,
  onViewAssignedProfiles = null,
  onViewAssignedTasks = null,
  onOpenMergeHistory = null,
  onOpenCsvImport = null,
  onOpenBulkExport = null,
  onOpenFutureProspects = null,
  futureProspectsLabel = "Future Prospects",
}) {
  const canViewTasks = useMemo(() => hasPermission(user, "tasks", "view"), [user]);
  const [summary, setSummary] = useState({
    total: 0,
    male: 0,
    female: 0,
    loading: true,
  });
  const [assignedProfileSummary, setAssignedProfileSummary] = useState({
    total: 0,
    loading: true,
  });
  const [assignedTaskSummary, setAssignedTaskSummary] = useState({
    total: 0,
    loading: true,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      setError("");
      setSummary((current) => ({ ...current, loading: true }));
      setAssignedProfileSummary((current) => ({ ...current, loading: true }));
      setAssignedTaskSummary((current) => ({ ...current, loading: true }));

      try {
        const profileRequest = API.get("/profiles/search?include_meta=1&limit=1&offset=0");
        const assignedProfilesRequest = user?.id
          ? API.get("/profiles/assigned-to-me?include_meta=1&limit=1&offset=0")
          : Promise.resolve({ data: {} });
        const assignedTasksRequest = user?.id && canViewTasks
          ? API.get("/tasks/dashboard")
          : Promise.resolve({ data: {} });

        const [profileRes, assignedProfilesRes, assignedTasksRes] = await Promise.all([
          profileRequest,
          assignedProfilesRequest,
          assignedTasksRequest,
        ]);

        if (!active) return;

        const profilePayload = normalizeCollectionPayload(profileRes.data);
        const profileItems = Array.isArray(profilePayload.items) ? profilePayload.items : [];
        const total = Number(profilePayload.total || 0);
        const male = Number(
          profilePayload.stats?.male ??
          profileItems.filter((item) => String(item?.gender || "").trim().toLowerCase() === "male").length
        );
        const female = Number(
          profilePayload.stats?.female ??
          profileItems.filter((item) => String(item?.gender || "").trim().toLowerCase() === "female").length
        );

        const assignedProfilesPayload = normalizeCollectionPayload(assignedProfilesRes.data);
        const assignedTasksPayload = assignedTasksRes.data || {};
        const assignedPending = Number(assignedTasksPayload.my_pending || 0);
        const assignedInProgress = Number(assignedTasksPayload.my_in_progress || 0);
        const assignedCompleted = Number(assignedTasksPayload.my_completed || 0);

        setSummary({
          total,
          male,
          female,
          loading: false,
        });
        setAssignedProfileSummary({
          total: Number(assignedProfilesPayload.total || 0),
          loading: false,
        });
        setAssignedTaskSummary({
          total: assignedPending + assignedInProgress + assignedCompleted,
          loading: false,
        });
      } catch (err) {
        console.error("Failed to load dashboard summary", err);
        if (!active) return;
        setSummary({
          total: 0,
          male: 0,
          female: 0,
          loading: false,
        });
        setAssignedProfileSummary({
          total: 0,
          loading: false,
        });
        setAssignedTaskSummary({
          total: 0,
          loading: false,
        });
        setError(err.response?.data?.error || "Failed to load dashboard counts.");
      }
    };

    loadDashboard();

    return () => {
      active = false;
    };
  }, [user?.id, canViewTasks]);

  const actions = [
    { key: "duplicates", label: "Duplicate Merge History", onClick: onOpenMergeHistory },
    { key: "import", label: "CSV Import", onClick: onOpenCsvImport },
    { key: "export", label: "Bulk Export", onClick: onOpenBulkExport },
    { key: "future", label: futureProspectsLabel, onClick: onOpenFutureProspects },
  ].filter((item) => typeof item.onClick === "function");

  return (
    <section style={S.page}>
      <style>{`
        @keyframes jkDashboardRise {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 980px) {
          .jk-dashboard-metrics {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .jk-dashboard-actions {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 640px) {
          .jk-dashboard-metrics,
          .jk-dashboard-assigned,
          .jk-dashboard-actions {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div style={S.shell}>
        {error ? <div style={S.errorBox}>{error}</div> : null}

        <div style={S.panel}>
          <div style={S.sectionLabel}>Profile Overview</div>
          <div style={S.metricGrid} className="jk-dashboard-metrics">
            <MetricCard label="TOTAL" value={formatCount(summary.total, summary.loading)} />
            <MetricCard label="MALE" value={formatCount(summary.male, summary.loading)} />
            <MetricCard label="FEMALE" value={formatCount(summary.female, summary.loading)} />
          </div>

          <div style={S.assignedHeading}>ASSIGNED TO ME</div>
          <div style={S.assignedGrid} className="jk-dashboard-assigned">
            <MetricCard
              compact
              label="PROFILES"
              value={formatCount(assignedProfileSummary.total, assignedProfileSummary.loading)}
              onClick={typeof onViewAssignedProfiles === "function" ? onViewAssignedProfiles : undefined}
            />
            {canViewTasks && (
              <MetricCard
                compact
                label="TASKS"
                value={formatCount(assignedTaskSummary.total, assignedTaskSummary.loading)}
                onClick={typeof onViewAssignedTasks === "function" ? onViewAssignedTasks : undefined}
              />
            )}
          </div>
        </div>

        {actions.length > 0 && (
          <div style={S.actionsWrap}>
            <div style={S.sectionLabel}>Quick Actions</div>
            <div style={S.actionsGrid} className="jk-dashboard-actions">
              {actions.map((action) => (
                <ActionButton key={action.key} label={action.label} onClick={action.onClick} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const S = {
  page: {
    animation: "jkDashboardRise 0.28s ease both",
  },
  shell: {
    display: "grid",
    gap: 22,
  },
  panel: {
    padding: 0,
  },
  sectionLabel: {
    marginBottom: 16,
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 1.3,
    textTransform: "uppercase",
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },
  assignedHeading: {
    margin: "22px 0 12px",
    color: BRAND.red,
    textAlign: "center",
    fontSize: 16,
    fontWeight: 900,
    letterSpacing: 1.3,
  },
  assignedGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  metricCard: {
    minHeight: 120,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "20px 18px",
    borderRadius: 18,
    border: `1px solid ${BRAND.border}`,
    background: BRAND.panel,
    boxShadow: "0 18px 42px rgba(185,52,58,0.07)",
    textAlign: "center",
  },
  metricCardCompact: {
    minHeight: 96,
  },
  metricCardButton: {
    cursor: "pointer",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
  },
  metricValue: {
    color: BRAND.red,
    fontSize: 42,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: -1.4,
  },
  metricValueCompact: {
    fontSize: 24,
    letterSpacing: -0.6,
  },
  metricLabel: {
    color: "#586179",
    fontSize: 22,
    lineHeight: 1.1,
    fontWeight: 900,
    letterSpacing: 1.1,
  },
  metricLabelCompact: {
    fontSize: 18,
  },
  actionsWrap: {
    paddingTop: 6,
  },
  actionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
  },
  actionButton: {
    minHeight: 72,
    padding: "18px 20px",
    borderRadius: 18,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "rgba(255,255,255,0.96)",
    color: BRAND.red,
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 14px 32px rgba(185,52,58,0.08)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
  },
  errorBox: {
    padding: "14px 16px",
    borderRadius: 16,
    border: `1px solid rgba(215,60,46,0.18)`,
    background: "rgba(255,242,239,0.92)",
    color: BRAND.redDeep,
    fontWeight: 700,
  },
};
